import { useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import type { LatestPlanSnapshot, PlanProfile, RecoveryMetricsMap, WearableConnection } from "../types";
import { providerLabel } from "../utils";

interface RecoveryPageProps {
  snapshot: LatestPlanSnapshot | null;
  wearables: WearableConnection[];
  metrics: RecoveryMetricsMap;
  connectWearable: (provider: string, code: string) => Promise<void>;
  disconnectWearable: (provider: string) => Promise<void>;
  refreshWearables: () => Promise<void>;
  rebuildPlan: (profile: PlanProfile, trigger: string) => Promise<LatestPlanSnapshot>;
  confirmDisconnect: (provider: string) => Promise<boolean>;
  onCreatePlan: () => void;
  onError: (message: string) => void;
}

export function RecoveryPage({ snapshot, wearables, metrics, connectWearable, disconnectWearable, refreshWearables, rebuildPlan, confirmDisconnect, onCreatePlan, onError }: RecoveryPageProps) {
  const [dialogProvider, setDialogProvider] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [adapting, setAdapting] = useState(false);

  async function connect(item: WearableConnection) {
    if (!item.configured) {
      try { await connectWearable(item.provider, "web-demo"); } catch (caught) { onError(message(caught)); }
      return;
    }
    setCode("");
    setDialogProvider(item.provider);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!dialogProvider) return;
    setConnecting(true);
    try {
      await connectWearable(dialogProvider, code.trim());
      setDialogProvider(null);
    } catch (caught) {
      onError(message(caught));
    } finally {
      setConnecting(false);
    }
  }

  async function disconnect(provider: string) {
    if (!(await confirmDisconnect(provider))) return;
    try { await disconnectWearable(provider); } catch (caught) { onError(message(caught)); }
  }

  async function adapt() {
    if (!snapshot) { onCreatePlan(); return; }
    setAdapting(true);
    try { await rebuildPlan(snapshot.profile, "recoveryDeviation"); } catch (caught) { onError(message(caught)); } finally { setAdapting(false); }
  }

  return (
    <section className="route active-route">
      <header className="page-header"><div><p className="eyebrow">CONNECTED HEALTH</p><h1>Recovery</h1></div></header>
      <section className="card recovery-hero"><div><h2>Train with your current capacity</h2><p className="muted">Connect supported services, review normalized recovery signals, and use them when adapting your plan.</p></div><button className="button primary" type="button" disabled={adapting} onClick={() => void adapt()}><Icon name="refresh" />{adapting ? "Adapting…" : "Adapt from recovery"}</button></section>

      <div className="wearables-grid">
        {wearables.map((item) => {
          const value = metrics[item.provider];
          const stats = value ? [
            ["Readiness", value.readiness_score == null ? null : `${value.readiness_score}/100`],
            ["Sleep", value.sleep_hours == null ? null : `${value.sleep_hours} h`],
            ["HRV", value.hrv == null ? null : `${value.hrv} ms`],
            ["Resting HR", value.resting_hr == null ? null : `${value.resting_hr} bpm`],
            ["Weekly distance", value.weekly_distance_km == null ? null : `${value.weekly_distance_km} km`],
          ].filter((entry): entry is [string, string] => entry[1] != null) : [];
          return (
            <section className="card wearable-card" key={item.provider}>
              <div className="wearable-head"><div className="provider-mark">{providerLabel(item.provider).slice(0, 1)}</div><div><h2>{providerLabel(item.provider)}</h2><span className={`connection-label ${item.connected ? "connected" : ""}`}>{item.connected ? "Connected" : item.configured ? "Ready to connect" : "Demo mode"}</span></div></div>
              <div className="provider-stats">
                {value?.error ? <p className="error">{value.error}</p> : stats.length ? stats.map(([label, stat]) => <div key={label}><span>{label}</span><strong>{stat}</strong></div>) : <p className="muted">{item.connected ? "No metrics available yet." : "Connect to add recovery context to adaptive planning."}</p>}
              </div>
              <div className="card-actions">
                {item.connected ? <><button className="button secondary" type="button" onClick={() => void refreshWearables()}><Icon name="refresh" />Refresh</button><button className="button text danger-text" type="button" onClick={() => void disconnect(item.provider)}>Disconnect</button></> : <button className="button primary" type="button" onClick={() => void connect(item)}><Icon name="link" />{item.configured ? "Connect" : "Connect demo"}</button>}
              </div>
            </section>
          );
        })}
      </div>

      <Modal open={Boolean(dialogProvider)} title={`Connect ${providerLabel(dialogProvider || "")}`} eyebrow="CONNECT SERVICE" onClose={() => setDialogProvider(null)}>
        <form className="form-stack" onSubmit={submit}>
          <p className="muted">Complete the provider authorization flow, then paste its returned authorization code.</p>
          <label className="field">Authorization code<input required autoComplete="off" value={code} onChange={(event) => setCode(event.target.value)} /></label>
          <footer className="modal-footer"><button className="button text" type="button" onClick={() => setDialogProvider(null)}>Cancel</button><button className="button primary" type="submit" disabled={connecting}>{connecting ? "Connecting…" : "Connect"}</button></footer>
        </form>
      </Modal>
    </section>
  );
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : "The operation failed.";
}
